/**
 * Bynder category model — ported verbatim from the legacy picker
 * (BYN_CATS / BYN_STOCK / filtersFor). Categories map to Bynder metaproperty
 * READABLE names; `div: true` categories are additionally scoped to the
 * Step-1 division via `Division_Code:<CODE>`.
 */
export interface BynderCategory {
  label: string;
  prop: string;
  opt: string;
  /** scope to the current division when one is picked */
  div?: boolean;
}

export const BYN_CATS: readonly BynderCategory[] = [
  { label: "Exteriors", prop: "Plan_Asset_Type", opt: "Exterior", div: true },
  { label: "Interiors", prop: "Plan_Asset_Type", opt: "Interiors", div: true },
  { label: "Everything's Included (EI)", prop: "Plan_Asset_Type", opt: "True_EI", div: true },
  { label: "Streetscapes", prop: "Masterplan_Community", opt: "Streetscape", div: true },
  { label: "Amenities", prop: "Masterplan_Community", opt: "Amenity", div: true },
  { label: "Community Aerials", prop: "Masterplan_Community", opt: "Aerial", div: true },
  { label: "Events", prop: "Masterplan_Community", opt: "Event", div: true },
  { label: "5-Star Imagery", prop: "Rating", opt: "_____", div: true },
  { label: "Active Adult", prop: "Lennar_Brands___Campaigns", opt: "Active_Adult___55_", div: true },
  { label: "Next Gen", prop: "Next_Gen", opt: "Next_Gen", div: true },
  { label: "Solar", prop: "Solar", opt: "Yes", div: true },
];

export const BYN_STOCK: readonly BynderCategory[] = [
  { label: "Lifestyle", prop: "Stock", opt: "Lifestyle" },
  { label: "Market / City", prop: "Stock", opt: "Market___City" },
  { label: "Design Elements", prop: "Stock", opt: "Design_Elements___Backgrounds" },
];

/** legacy filtersFor — AND-combined readable-name filters for /bynder-search */
/** Derive readable alt text from a Bynder asset name when the asset has no Alt Text
    (monolith window.__bynAltFromName): "ATL_WillowCreek_Pic_Exterior_Front_1of2"
    → "Willow Creek Exterior Front". */
export function bynAltFromName(n?: string): string {
  if (!n) return "";
  const base = String(n).replace(/\.[a-z0-9]+$/i, "");
  const p = base.split(/[_\s]+/).filter(
    (s) => s && !/^[A-Z0-9]{2,4}$/.test(s) && !/^(Pic|Rend|Rendering|Photo|Res|Img)$/i.test(s) && !/^\d+of\d+$/i.test(s) && !/^F\d+$/i.test(s),
  );
  return p.join(" ").replace(/([a-z])([A-Z])/g, "$1 $2").replace(/\s+/g, " ").trim();
}

/**
 * ── THE ALT TEXT A BYNDER PICK ACTUALLY DESERVES (2026-08-27) ──────────────────
 *
 * Division report, via Layne's Review Board: "Title and alt text for images selected
 * from Bynder are incorrect." It was, and here is the measured reason.
 *
 * The backend builds `alt` as `property_Alt_Text[0] || description`, and Lennar's
 * Bynder has NO alt-text metaproperty values filled in — while `description`, on
 * every asset migrated out of Box, holds the migration note:
 *
 *   "Migrated from Box. Original path: Digital_Assets/Digital_Marketing_Asset_Library_
 *    Staging_Folder/ATL_Staging/Social_Video_Captivate/Captivate Static/CHT"
 *
 * Measured live from the QA app on 2026-08-27 across four categories, 116 assets:
 *   Exteriors 30 → 20 migration notes, 10 empty, 0 real
 *   Interiors 30 → 19 migration notes, 11 empty, 0 real
 *   Amenities 26 →  8 migration notes, 18 empty, 0 real
 *   Lifestyle 30 → 28 migration notes,  2 empty, 0 real
 * Zero assets carry usable alt text. So every pick with a description shipped a Box
 * folder path as its alt — truncated at the field's 100-char cap, mid-path, which is
 * why it read as garbled rather than merely wrong.
 *
 * `bynAlt` is now the ONE place a pick's alt is decided, so the two call sites in the
 * Inspector cannot drift. It distrusts the feed's alt (this ships before any backend
 * change, and a DAM's free-text description is not alt text even when it isn't a
 * migration note) and falls back to the asset NAME, which is structured and reliable.
 */

/** a description that is machinery, not a description of the picture */
const JUNK_ALT = /migrated from box|original path|digital[_ ]assets\/|\/staging|dropbox|sharepoint/i;

/** true when the feed's alt is unusable as alt text */
export function bynAltJunk(alt?: string): boolean {
  const v = String(alt || "").trim();
  if (!v) return true;
  if (JUNK_ALT.test(v)) return true;
  /* a path or a filename rather than a sentence: slashes/underscores and no spaces */
  if (!/\s/.test(v) && /[/\\_]/.test(v)) return true;
  return false;
}

/** cut at `max` on a WORD boundary, so a clamp never ends mid-word (2026-08-27) */
export function altClamp(s: string, max: number): string {
  const v = String(s || "").replace(/\s+/g, " ").trim();
  if (!max || v.length <= max) return v;
  const cut = v.slice(0, max);
  const sp = cut.lastIndexOf(" ");
  return (sp > max * 0.5 ? cut.slice(0, sp) : cut).replace(/[\s,;:.-]+$/, "");
}

/**
 * The alt text for a picked asset, capped. Feed alt when it is genuinely descriptive,
 * otherwise derived from the asset name; empty only when neither says anything.
 */
export function bynAlt(asset?: { name?: string; alt?: string }, max = 100): string {
  const a = asset || {};
  const feed = bynAltJunk(a.alt) ? "" : String(a.alt || "").trim();
  return altClamp(feed || bynAltFromName(a.name) || "", max);
}

export function filtersFor(c: BynderCategory, divisionCode?: string): string {
  const p: string[] = [];
  if (c.div && divisionCode) p.push("Division_Code:" + divisionCode);
  p.push(c.prop + ":" + c.opt);
  return p.join(";");
}
